// Multiplayer client: WebSocket connection, rooms, and a local mirror of the race interpolated from server snapshots.
import { Race, applyCold, applyHot, decodeSnapshot, type LateSpec, type RaceEvent, type RaceOptions, type Snapshot } from '@gruvetto/core';
import type { RoomState, ServerMessage } from '@gruvetto/contracts';
import { state } from '../data/store.js';
import { accessToken } from '../data/api.js';
import { t } from '../i18n/index.js';

const detailOf = <T>(e: Event): T => (e as CustomEvent<T>).detail;

const INTERP_DELAY = 120; // ms of deliberate delay to interpolate between two snapshots

export function defaultServerUrl() {
  return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;
}

type NetStatus = 'off' | 'connecting' | 'on';

/** Delay before reconnection attempt `n` (0-based): 0.5 s, 1 s, 2 s… capped at 4 s. */
export const retryDelay = (n: number) => Math.min(4000, 500 * 2 ** n);
/** How long to wait for the server to answer a `resume` before trying again. */
const RESUME_REPLY_MS = 3000;
/** Close code "Service Restart": the server instance is being replaced, the next connection reaches the new one. */
const CLOSE_RESTART = 1012;
/** Connection attempts after a restart close, with retryDelay between them. */
const RESTART_ATTEMPTS = 6;
/** Attempts of a first connection before the player is told it failed: about 7 s with retryDelay between them. */
const CONNECT_ATTEMPTS = 5;

class NetClient extends EventTarget {
  /** Resume token of our slot in the room (rotated by the server), and how long the slot is held. */
  private resumeToken: string | null = null;
  private graceMs = 15000;
  private closing = false;
  /** True while we try to get our slot back after an unexpected drop. */
  reconnecting = false;
  private retryT: ReturnType<typeof setTimeout> | undefined;
  /** Timer of the reconnection after a restart close; separate from retryT, which giveUp() clears on the close that follows a failed attempt. */
  private restartT: ReturnType<typeof setTimeout> | undefined;
  /** Bumped by disconnect(): a connectRetrying() still waiting stops there. */
  private connectGen = 0;
  /** Socket of the resume attempt in flight, closed if the player leaves meanwhile. */
  private pending: WebSocket | null = null;
  eventCtx: { def: { id: string }; cat: string } | null = null; me: string | null; ping: number | null; pingT: ReturnType<typeof setInterval> | undefined; publicUrl: string | null; room: RoomState | null; status: NetStatus; ws: WebSocket | null;
  /** Options of the race in progress in our room (`start` received): a screen mounted after the message can still launch it. */
  lastStart: Record<string, unknown> | null = null;
  /** Running totals of what the server sent (bytes of text, snapshots): the performance overlay takes the difference. */
  readonly stats = { bytes: 0, snaps: 0 };
  constructor() { super(); this.ws = null; this.status = 'off'; this.me = null; this.room = null; this.publicUrl = null; this.ping = null; }

  connect(url = defaultServerUrl()) {
    if (this.ws && this.ws.readyState <= 1) return Promise.resolve();
    this.setStatus('connecting');
    return new Promise<void>((resolve, reject) => {
      let ws: WebSocket;
      try { ws = new WebSocket(url); } catch (e) { this.setStatus('off'); return reject(e); }
      ws.binaryType = 'arraybuffer'; // snapshots arrive as binary frames
      this.ws = ws;
      const fail = () => { this.setStatus('off'); reject(new Error(t.net.cannotReach)); };
      ws.onerror = fail;
      ws.onopen = () => { ws.onerror = null; this.setStatus('on'); this.hello(); this.pingLoop(); resolve(); };
      ws.onclose = ev => this.dropped(url, ev?.code);
      ws.onmessage = ev => this.onMessage(ev);
    });
  }
  /** Connects for a screen: a missed attempt (a server being replaced, a network blip) is tried again before any error shows. */
  async connectRetrying(url = defaultServerUrl()) {
    const gen = this.connectGen;
    for (let n = 0; ; n++) {
      try {
        return await this.connect(url);
      } catch (e) {
        if (n >= CONNECT_ATTEMPTS - 1 || gen !== this.connectGen) throw e;
      }
      await new Promise(done => setTimeout(done, retryDelay(n)));
      if (gen !== this.connectGen) throw new Error(t.net.cannotReach);
    }
  }
  /**
   * The server closed us for a restart (ADR 0015): our room, if any, is gone with the old instance (it said so before
   * closing), so we come back as a fresh connection to the new one, after a short wait and a few tries.
   */
  private reconnectAfterRestart(url: string, n = 0) {
    if (n >= RESTART_ATTEMPTS || this.closing || this.status !== 'off') return;
    this.restartT = setTimeout(() => { this.connect(url).catch(() => this.reconnectAfterRestart(url, n + 1)); }, retryDelay(n));
  }
  private onMessage(ev: MessageEvent) {
    if (ev.data instanceof ArrayBuffer) {
      // a binary frame is a snapshot (core race-net-binary.ts); one the client cannot read is dropped, the next full one resyncs
      this.stats.bytes += ev.data.byteLength; this.stats.snaps++;
      let s: Snapshot;
      try { s = decodeSnapshot(ev.data); } catch { return; }
      this.emit('snap', { t: 'snap', s });
      return;
    }
    const m = JSON.parse(ev.data) as ServerMessage;
    this.stats.bytes += typeof ev.data === 'string' ? ev.data.length : 0;
    if (m.t === 'welcome') { this.me = m.id; this.publicUrl = m.publicUrl; }
    if (m.t === 'room') { this.room = m.room; if (!m.room) { this.resumeToken = null; this.lastStart = null; } }
    if (m.t === 'start') this.lastStart = m.opts;
    if (m.t === 'end') this.lastStart = null;
    if (m.t === 'session') { this.resumeToken = m.token; this.graceMs = m.graceMs; }
    if (m.t === 'pong') this.ping = Math.round(performance.now() - m.c);
    this.emit(m.t, m);
  }
  /** The socket closed: try to resume our slot if we were in a room, otherwise (or if that fails) give up as before. */
  private dropped(url: string, code?: number) {
    clearInterval(this.pingT);
    const deliberate = this.closing; this.closing = false;
    if (!deliberate && code !== CLOSE_RESTART && this.resumeToken && this.room) { this.setStatus('connecting'); this.retry(url, Date.now() + this.graceMs, 0); return; }
    this.giveUp();
    if (!deliberate && code === CLOSE_RESTART) this.reconnectAfterRestart(url);
  }
  private giveUp() {
    clearTimeout(this.retryT);
    if (this.pending) { const p = this.pending; this.pending = null; p.onclose = null; p.onmessage = null; p.close(); }
    this.resumeToken = null; this.setReconnecting(false);
    this.setStatus('off'); this.room = null; this.emit('room', { room: null });
  }
  private setReconnecting(on: boolean) { if (this.reconnecting !== on) { this.reconnecting = on; this.emit('reconnecting', { active: on }); } }
  private retry(url: string, deadline: number, n: number) {
    this.setReconnecting(true);
    const delay = retryDelay(n);
    if (Date.now() + delay >= deadline) return this.giveUp();
    this.retryT = setTimeout(() => {
      let ws: WebSocket;
      try { ws = new WebSocket(url); } catch { return this.retry(url, deadline, n + 1); }
      ws.binaryType = 'arraybuffer';
      this.pending = ws;
      let settled = false;
      const again = () => { if (settled) return; settled = true; clearTimeout(timer); this.retry(url, deadline, n + 1); };
      const timer = setTimeout(() => { ws.onclose = null; ws.close(); again(); }, RESUME_REPLY_MS);
      ws.onerror = () => { /* onclose follows */ };
      ws.onclose = again;
      ws.onopen = () => ws.send(JSON.stringify({ t: 'resume', token: this.resumeToken, auth: accessToken() }));
      ws.onmessage = ev => {
        if (ev.data instanceof ArrayBuffer) { if (settled) this.onMessage(ev); return; } // a snapshot: only once resumed
        const m = JSON.parse(ev.data) as ServerMessage;
        if (m.t === 'error') { settled = true; clearTimeout(timer); ws.onclose = null; ws.close(); return this.giveUp(); } // token refused: fall back to leaving the room
        if (m.t === 'session' && !settled) {
          settled = true; clearTimeout(timer); this.pending = null;
          this.ws = ws; ws.onclose = ev => this.dropped(url, ev?.code); ws.onmessage = e => this.onMessage(e);
          this.setStatus('on'); this.setReconnecting(false); this.pingLoop();
        }
        if (settled) this.onMessage(ev);
      };
    }, delay);
  }
  setStatus(s: NetStatus) { this.status = s; this.emit('status', { status: s }); }
  emit(t: string, detail: unknown) { this.dispatchEvent(new CustomEvent(t, { detail })); }
  send(t: string, data: object = {}) { if (this.ws?.readyState === 1) this.ws.send(JSON.stringify({ t, ...data })); }
  hello() { const p = state.profile, c = state.club; this.send('hello', { name: p.name, color: p.color, mass: p.mass, ftp: p.ftp, look: p.look, assist: state.settings.assist !== false, token: accessToken(), tag: c?.tag ?? null, club: c?.id ?? null, country: p.country ?? null }); }
  pingLoop() { clearInterval(this.pingT); this.pingT = setInterval(() => this.send('ping', { c: performance.now() }), 3000); }
  disconnect() {
    clearTimeout(this.retryT); clearTimeout(this.restartT); this.connectGen++;
    // While reconnecting the old socket is already closed and fires no close event: stop the attempts and leave right away.
    if (this.reconnecting) return this.giveUp();
    this.closing = true; this.ws?.close();
  }
  get isHost() { return !!this.room && this.room.hostId === this.me; }
  /** We watch the room as a supporter (no seat, no rider). */
  get isSpectator() { return !!this.room && !!this.me && !!this.room.spectators?.some(s => s.id === this.me); }
}

export const net = new NetClient();
// access point for the end-to-end test (connection state, room, role); guarded for the Node test environment
if (typeof window !== 'undefined') (window as unknown as { __gvNet: NetClient }).__gvNet = net;

/** Race as seen by a client: same seed as the server, state overwritten by the interpolated snapshots. */
export class NetRace {
  buf: { at: number; s: Snapshot }[]; ended: unknown[] | null; me: Race['riders'][number]; onEnd: (e: Event) => void; onEv: (e: Event) => void; onLate: (e: Event) => void; onSnap: (e: Event) => void; race: Race; stageRes: unknown = null;
  /** The game server's signed results (ADR 0022): the activity and the event stage carry them to the API. */
  receipt: string | null = null;
  /** Options the server built the race with: kept for the replay of this race. */
  opts: RaceOptions;
  /** `meId`: our player id, or null for a supporter (the mirror then follows the first rider until the view picks one). */
  constructor(opts: RaceOptions, meId: string | null) {
    this.opts = opts;
    this.race = new Race(opts);
    this.race.online = true;
    this.me = (meId && this.race.byPid(meId)) || this.race.riders[0];
    this.race.human = this.me;
    this.buf = [];
    this.ended = null;
    // the cold part (gear, item, effects, out, finish…) is applied on receipt so no change is missed; the hot part
    // (positions) waits for render time and is interpolated (update)
    this.onSnap = (e: Event) => { const s = detailOf<{ s: Snapshot }>(e).s; applyCold(this.race, s); this.buf.push({ at: performance.now(), s }); if (this.buf.length > 30) this.buf.shift(); };
    this.onEv = (e: Event) => this.race.events.push(...detailOf<{ e: RaceEvent[] }>(e).e);
    this.onEnd = (e: Event) => { const d = detailOf<{ results: unknown[]; stage?: unknown; receipt?: string }>(e); this.ended = d.results; this.stageRes = d.stage || null; this.receipt = d.receipt ?? null; };
    // a rider who joined in progress: appended after the field, at the same index as on the server (the next snapshot carries them)
    this.onLate = (e: Event) => { const d = detailOf<{ human: LateSpec; id: number }>(e); if (d.id === this.race.riders.length) this.race.addHuman(d.human, d.human.startT); };
    net.addEventListener('snap', this.onSnap); net.addEventListener('ev', this.onEv); net.addEventListener('end', this.onEnd); net.addEventListener('late', this.onLate);
  }
  /** Positions the race at render time (now − interpolation delay). */
  update() {
    const b = this.buf;
    if (!b.length) return;
    const tr = performance.now() - INTERP_DELAY;
    let i = b.length - 1;
    while (i > 0 && b[i - 1].at > tr) i--;
    const nxt = b[i], prv = b[i - 1];
    if (prv && nxt.at > prv.at && tr >= prv.at) applyHot(this.race, nxt.s, Math.min(1, (tr - prv.at) / (nxt.at - prv.at)), prv.s);
    else applyHot(this.race, nxt.s);
    if (this.ended && this.race.state !== 'done') this.race.state = 'done';
  }
  /** What the connection received so far (bytes, snapshots): the performance overlay and the end-to-end test. */
  get stats() { return net.stats; }
  /** `typed`: keyboard watts (an administrator's test), which the server leaves out of ratings and signed results. */
  sendInput(power: number, cadence: number, hr?: number, typed = false) { net.send('in', { p: Math.round(power), c: Math.round(cadence), hr: Math.round(hr || 0), ...(typed ? { k: true } : {}) }); }
  action(a: string) { net.send('act', { a }); }
  /** Ends this rider's race: the room stays, and coming back to its screen must not launch the race again. */
  retire() { net.lastStart = null; net.send('retire'); }
  dispose() { net.removeEventListener('snap', this.onSnap); net.removeEventListener('ev', this.onEv); net.removeEventListener('end', this.onEnd); net.removeEventListener('late', this.onLate); }
}
